import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import type { BrandingConfig } from '../types/branding.types';
import { generatePalette, generateDarkPalette } from '../utils/palette-generator';
import { getSubdomain, getOrgSlugFromQuery } from '../utils/subdomain-resolver';
import brandingService from '../services/branding.service';

interface BrandingContextValue {
  config: BrandingConfig | null;
  isLoading: boolean;
  isResolved: boolean;
  /** Call after login when org_id is known */
  loadForOrg: (orgId: number, orgSlug?: string) => Promise<void>;
  /** Reset to default AB3 branding (session only, does not delete org config) */
  resetToDefault: () => void;
  /** Get org_slug for logout redirect */
  orgSlug: string | null;
}

const BrandingContext = createContext<BrandingContextValue | null>(null);

const DEFAULT_APP_NAME = 'Assistant Business 3.0';

function applyPaletteVars(root: HTMLElement, prefix: string, hex: string, isDark: boolean) {
  const palette = generatePalette(hex);
  for (const [shade, value] of Object.entries(palette)) {
    root.style.setProperty(`--${prefix}-${shade}`, value);
  }
  if (isDark) {
    const dark = generateDarkPalette(hex);
    for (const [shade, value] of Object.entries(dark)) {
      root.style.setProperty(`--${prefix}-${shade}`, value);
    }
  }
}

function applyBranding(config: BrandingConfig) {
  const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
  const root = document.documentElement;

  applyPaletteVars(root, 'primary', config.primary_color, isDark);
  if (config.accent_color) {
    applyPaletteVars(root, 'accent', config.accent_color, isDark);
  }

  document.title = config.app_name || DEFAULT_APP_NAME;

  if (config.favicon_url) {
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = config.favicon_url;
  }
}

function clearBranding() {
  const root = document.documentElement;
  const shades = ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950'];
  for (const shade of shades) {
    root.style.removeProperty(`--primary-${shade}`);
    root.style.removeProperty(`--accent-${shade}`);
  }
  document.title = DEFAULT_APP_NAME;
}

export const BrandingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [config, setConfig] = useState<BrandingConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isResolved, setIsResolved] = useState(false);
  const configRef = useRef<BrandingConfig | null>(null);

  // Re-apply palette when dark mode toggles
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.attributeName === 'data-theme' && configRef.current) {
          applyBranding(configRef.current);
        }
      }
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  // Keep ref in sync
  useEffect(() => {
    configRef.current = config;
  }, [config]);

  // Apply branding when config changes
  useEffect(() => {
    if (config) {
      applyBranding(config);
      brandingService.setCache(config);
    }
  }, [config]);

  // Initial resolution: subdomain or query param (pre-auth)
  useEffect(() => {
    let cancelled = false;

    async function resolveInitial() {
      // Step 1: subdomain
      const subdomain = getSubdomain();
      if (subdomain) {
        const result = await brandingService.resolve({ domain: subdomain });
        if (!cancelled && result) {
          setConfig(result);
          setIsLoading(false);
          setIsResolved(true);
          return;
        }
      }

      // Step 2a: ?preview= token
      const params = new URLSearchParams(window.location.search);
      const previewToken = params.get('preview');
      if (previewToken) {
        const result = await brandingService.resolve({ slug: `preview:${previewToken}` });
        if (!cancelled && result) {
          setConfig(result);
          setIsLoading(false);
          setIsResolved(true);
          return;
        }
      }

      // Step 2b: ?org= query param
      const slug = getOrgSlugFromQuery();
      if (slug) {
        const result = await brandingService.resolve({ slug });
        if (!cancelled && result) {
          setConfig(result);
          setIsLoading(false);
          setIsResolved(true);
          return;
        }
      }

      // Step 4: localStorage cache (show while waiting for auth)
      const cached = subdomain ? null : brandingService.getCachedBySlug();
      if (!cancelled && cached?.config) {
        setConfig(cached.config);
      }

      if (!cancelled) {
        setIsLoading(false);
        // Not fully resolved until post-auth step runs
      }
    }

    resolveInitial();
    return () => { cancelled = true; };
  }, []);

  // Step 3: post-auth load by org_id
  const loadForOrg = useCallback(async (orgId: number, _orgSlug?: string) => {
    try {
      const result = await brandingService.getConfig(orgId);
      if (result) {
        setConfig(result);
        setIsResolved(true);
      } else {
        // No branding for this org — clear any cached branding
        setConfig(null);
        clearBranding();
        setIsResolved(true);
      }
    } catch (err) {
      console.warn('[branding] Failed to load config for org', orgId, err);
      // Keep whatever we have (cache or subdomain resolve)
      setIsResolved(true);
    }
  }, []);

  const resetToDefault = useCallback(() => {
    setConfig(null);
    clearBranding();
    brandingService.clearCache();
  }, []);

  const orgSlug = config?.org_slug ?? null;

  return (
    <BrandingContext.Provider value={{ config, isLoading, isResolved, loadForOrg, resetToDefault, orgSlug }}>
      {children}
    </BrandingContext.Provider>
  );
};

export function useBranding(): BrandingContextValue {
  const ctx = useContext(BrandingContext);
  if (!ctx) throw new Error('useBranding must be used within BrandingProvider');
  return ctx;
}

/**
 * Bridge component: place inside AuthProvider to trigger branding load after login.
 * Watches active organization id (from JWT via /auth/me-v2) and calls loadForOrg when it changes.
 */
export const BrandingAuthBridge: React.FC<{
  orgId: number | null | undefined;
  orgSlug?: string;
}> = ({ orgId, orgSlug }) => {
  const { loadForOrg } = useBranding();
  const prevOrgIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (orgId && orgId !== prevOrgIdRef.current) {
      prevOrgIdRef.current = orgId;
      loadForOrg(orgId, orgSlug);
    }
  }, [orgId, orgSlug, loadForOrg]);

  return null;
};
